<template>
  <div data-testid="booking-view" class="booking-view py-20 min-h-screen bg-white">
    <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="text-center mb-16">
        <p class="text-xs uppercase tracking-[0.3em] text-gray-500 mb-4">{{ $t('booking.subtitle') }}</p>
        <h1 class="text-2xl md:text-3xl font-light text-gray-900 mb-4">{{ $t('booking.title') }}</h1>
        <p class="text-gray-600 text-sm">{{ $t('booking.description') }}</p>
      </div>

      <!-- Stepper -->
      <BookingStepper
        :current-step="currentStep"
        :locked="currentStep === 4"
        @go-to-step="goToStep"
      />

      <!-- Step 1: Select (Cart Items) -->
      <div v-if="currentStep === 1" data-testid="booking-step-1">
        <div class="space-y-6">
          <!-- Adding Item Block - Always at top -->
          <div class="bg-stone-50 border border-dashed border-gray-300 overflow-hidden hover:border-gray-400 transition-colors">
            <div class="grid grid-cols-1 lg:grid-cols-12 gap-0">
              <!-- Left Box: Package Icon -->
              <div class="lg:col-span-3 p-6 flex flex-col items-center justify-center text-center border-b lg:border-b-0 lg:border-r border-gray-200">
                <div class="w-16 h-16 bg-white border border-gray-200 flex items-center justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
                <h3 class="text-base font-medium text-gray-900">{{ $t('booking.step_1.select_package') }}</h3>
                <p class="text-sm text-gray-500 mt-1">{{ $t('booking.step_1.select_package_desc') }}</p>
              </div>

              <!-- Center Box: Browse Packages Button -->
              <div class="lg:col-span-8 p-6 flex flex-col justify-center">
                <div class="flex justify-center items-center">
                  <router-link
                    to="/packages"
                    class="inline-flex items-center gap-2 px-8 py-4 bg-gray-900 text-white text-sm font-medium tracking-wide hover:bg-gray-800 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                    {{ $t('booking.step_1.browse_packages') }}
                  </router-link>
                </div>
                <p class="text-center text-gray-500 text-sm mt-4">
                  {{ $t('booking.step_1.browse_packages_instruction') }}
                </p>
              </div>

              <!-- Right Box: Empty -->
              <div class="lg:col-span-1 bg-gray-100/50 border-t lg:border-t-0 lg:border-l border-gray-200 flex items-center justify-center p-4">
                <span class="text-gray-400 text-xs uppercase tracking-wider">New</span>
              </div>
            </div>
          </div>

          <!-- Empty Cart Message (when no items) -->
          <div v-if="cartStore.isEmpty" data-testid="empty-cart" class="text-center py-16 bg-stone-50 border border-gray-100">
            <div class="w-16 h-16 mx-auto mb-4 bg-white border border-gray-200 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </div>
            <p class="text-xs uppercase tracking-[0.3em] text-gray-500 mb-2">Empty</p>
            <h2 class="text-lg font-medium text-gray-900 mb-2">{{ $t('booking.step_1.empty_cart') }}</h2>
            <p class="text-gray-600 text-sm">{{ $t('booking.step_1.empty_cart_desc') }}</p>
          </div>

          <!-- Cart Items -->
          <div
            v-for="item in sortedItems"
            :key="item.id"
            data-testid="cart-item"
            :data-package-code="item.package.code"
            class="bg-white border border-gray-200 overflow-hidden"
          >
            <div class="grid grid-cols-1 lg:grid-cols-12 gap-0">
              <!-- Left Box: Main Purpose -->
              <div class="lg:col-span-3 bg-stone-50 p-6 flex flex-col items-center justify-center text-center border-b lg:border-b-0 lg:border-r border-gray-200">
                <!-- Main Purpose Image -->
                <div class="w-20 h-20 mb-4 rounded-full overflow-hidden bg-white border border-gray-200">
                  <img
                    :src="getMainPurposeImage(item.package.main_purpose)"
                    :alt="item.package.main_purpose"
                    class="w-full h-full object-cover"
                  />
                </div>

                <!-- Main Purpose Title from packages.selector -->
                <h3 class="text-base font-medium text-gray-500 mb-1">
                  {{ $t(`packages.selector.${item.package.main_purpose}.title`) }}
                </h3>
                <p v-if="getAgeGenderLabel(item.package)" class="text-sm text-gray-500">
                  {{ getAgeGenderLabel(item.package) }}
                </p>
              </div>

              <!-- Center Box: Package Info -->
              <div class="lg:col-span-8 p-6">
                <!-- Has Package -->
                <template v-if="item.package_id && item.package_id !== 0">
                  <div class="flex items-start justify-between mb-4">
                    <div>
                      <div class="flex items-center gap-2 mb-2">
                        <span
                          :class="[
                            'px-2 py-1 text-xs font-medium uppercase tracking-wide',
                            item.package.package_type === 'premium'
                              ? 'bg-gradient-to-r from-[#60a5fa] via-[#81c4ff] to-[#60a5fa] text-white'
                              : 'bg-[#f6e7d3] text-gray-800'
                          ]"
                        >
                          {{ $t(`packages.filters.${item.package.package_type}`) }}
                        </span>
                      </div>
                      <h4 class="text-lg font-medium text-gray-900">{{ getLocalizedPackageName(item.package) }}</h4>
                    </div>
                    <div class="text-right">
                      <p class="text-xl font-medium text-gray-900">฿{{ formatPrice(item.package.price) }}</p>
                      <p class="text-xs text-gray-500">{{ $t('booking.step_1.summary.deposit') }}: ฿{{ calculateDeposit(item.package.services) }}</p>
                    </div>
                  </div>

                  <!-- Services -->
                  <div class="flex flex-wrap gap-2 mb-4">
                    <span
                      v-for="service in item.package.services"
                      :key="service"
                      class="inline-flex items-center px-2.5 py-1 bg-stone-50 text-gray-600 text-xs border border-gray-100"
                    >
                      {{ getLocalizedService(service) }}
                    </span>
                  </div>

                  <!-- Details -->
                  <div class="flex items-center gap-4 text-xs text-gray-500 mb-6">
                    <span class="flex items-center gap-1">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {{ item.package.duration_minutes }} {{ $t('booking.details.minutes') }}
                    </span>
                  </div>

                  <!-- Change Package Button -->
                  <button
                    @click="openChangePackageModal(item)"
                    data-testid="change-package"
                    class="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 text-xs font-medium border border-gray-200 hover:border-gray-400 px-3 py-2 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4 4" />
                    </svg>
                    {{ $t('booking.step_1.change_package') }}
                  </button>
                </template>

                <!-- No Package Selected -->
                <template v-else>
                  <div class="flex flex-col items-center justify-center py-8 text-center">
                    <div class="w-12 h-12 mb-4 bg-stone-50 border border-gray-200 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                    </div>
                    <p class="text-xs uppercase tracking-[0.3em] text-gray-500 mb-2">No Package</p>
                    <h4 class="text-base font-medium text-gray-900 mb-2">{{ $t('booking.step_1.no_package') }}</h4>
                    <p class="text-gray-500 text-sm mb-4">{{ $t('booking.step_1.select_package_desc') }}</p>
                    <button
                      @click="selectPackageForItem(item)"
                      class="inline-flex items-center justify-center px-6 py-2 bg-gray-900 text-white text-sm font-medium tracking-wide hover:bg-gray-800 transition-colors"
                    >
                      {{ $t('booking.step_1.select_package') }}
                    </button>
                  </div>
                </template>
              </div>

              <!-- Right Box: Delete -->
              <div class="lg:col-span-1 bg-stone-50 border-t lg:border-t-0 lg:border-l border-gray-200 flex lg:flex-col items-center justify-center p-4">
                <button
                  @click="confirmDelete(item)"
                  data-testid="remove-cart-item"
                  class="p-2 text-gray-400 hover:text-red-600 transition-colors"
                  :title="$t('booking.modals.delete.remove_title')"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- Photography Package Recommendation -->
          <div
            v-if="showPhotographyRecommendation"
            class="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 p-6"
          >
            <div class="flex flex-col sm:flex-row items-start gap-4">
              <div class="w-14 h-14 bg-white border border-blue-200 flex items-center justify-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div class="flex-1">
                <h3 class="text-base font-medium text-gray-900 mb-1">{{ $t('booking.step_1.photography_recommendation.title') }}</h3>
                <p class="text-sm text-gray-600 mb-4">{{ $t('booking.step_1.photography_recommendation.description') }}</p>
                <router-link
                  to="/packages/photography"
                  class="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-medium tracking-wide hover:bg-blue-700 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {{ $t('booking.step_1.photography_recommendation.cta') }}
                </router-link>
              </div>
            </div>
          </div>

          <!-- Photographer Jack Request -->
          <div class="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 p-6">
            <div class="flex flex-col sm:flex-row items-start gap-4">
              <div class="w-14 h-14 bg-white border border-amber-200 flex items-center justify-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </div>
              <div class="flex-1">
                <h3 class="text-base font-medium text-gray-900 mb-1">{{ $t('booking.step_1.photographer_jack_request.title') }}</h3>
                <p class="text-sm text-gray-600">{{ $t('booking.step_1.photographer_jack_request.description') }}</p>
              </div>
            </div>
          </div>

          <!-- Summary & Actions -->
          <div v-if="!cartStore.isEmpty" class="bg-white border border-gray-200 p-6 mt-8">
            <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <div class="flex items-baseline gap-6">
                  <div>
                    <p class="text-xs text-gray-500 uppercase tracking-wider">{{ $t('booking.step_1.summary.total_price') }}</p>
                    <p data-testid="cart-total" class="text-2xl font-medium text-gray-900">฿{{ cartStore.totalPrice.toLocaleString() }}</p>
                  </div>
                  <div>
                    <p class="text-xs text-gray-500 uppercase tracking-wider">{{ $t('booking.step_1.summary.deposit') }}</p>
                    <div class="flex items-baseline gap-2">
                      <p data-testid="cart-deposit" class="text-2xl font-medium text-gray-900">฿{{ cartStore.totalDeposit.toLocaleString() }}</p>
                      <span
                        v-if="cartStore.rawTotalDeposit > cartStore.totalDeposit"
                        class="text-sm text-gray-400 line-through"
                      >
                        ฿{{ cartStore.rawTotalDeposit.toLocaleString() }}
                      </span>
                    </div>
                    <p class="text-xs text-gray-500 mt-1">
                      ({{ $t('booking.step_1.summary.max_deposit') }})
                    </p>
                  </div>
                </div>
              </div>
              <div class="flex flex-col sm:flex-row gap-3">
                <router-link
                  to="/packages"
                  class="inline-flex items-center justify-center px-6 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium tracking-wide hover:bg-gray-50 transition-colors text-center"
                >
                  {{ $t('booking.step_1.summary.add_more') }}
                </router-link>
                <button
                  @click="goToStep(2)"
                  :disabled="!canProceedToAppoint"
                  data-testid="continue-to-appointment"
                  class="inline-flex items-center justify-center px-6 py-2.5 bg-gray-900 text-white text-sm font-medium tracking-wide hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-center"
                >
                  {{ $t('booking.step_1.summary.continue_to_appoint') }}
                </button>
              </div>
            </div>
          </div>

          <!-- Warning if items have no package -->
          <div v-if="hasItemsWithoutPackage" class="bg-stone-50 border border-gray-200 p-4 text-sm text-gray-700">
            <p class="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              {{ $t('booking.step_1.warning') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Step 2: Appoint (Date & Time) -->
      <div v-if="currentStep === 2" data-testid="booking-step-2" class="max-w-3xl mx-auto">
        <!-- Step Error (from backend validation) -->
        <div v-if="stepError" class="mb-6 bg-red-50 border border-red-200 p-4">
          <div class="flex items-start gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 class="text-sm font-medium text-red-800">{{ stepError }}</h3>
              <p class="text-xs text-red-600 mt-1">Please select a new time slot.</p>
            </div>
          </div>
        </div>

        <!-- Selected Date & Time Summary -->
        <div v-if="selectedDate || selectedSlot" class="mb-6 bg-stone-50 border border-gray-200 p-4">
          <h3 class="text-xs font-medium text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {{ $t('booking.step_2.your_selection') }}
          </h3>
          <div class="flex flex-wrap gap-4">
            <div v-if="selectedDate" class="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span data-testid="selected-date" class="text-gray-900 font-medium text-sm">{{ formatDisplayDate(selectedDate) }}</span>
            </div>
            <div v-if="selectedSlot" class="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span data-testid="selected-studio-time" class="text-gray-900 font-medium text-sm">{{ $t('booking.step_2.arrive_at_studio', { time: selectedSlot.studio_time }) }}</span>
              <span data-testid="selected-photo-time" class="text-gray-500 text-xs">({{ $t('booking.step_2.photo') }}: {{ selectedSlot.photo_time || '-' }})</span>
            </div>
          </div>
        </div>

        <!-- Calendar -->
        <div class="mb-6 border border-gray-200 p-6">
          <label class="block text-xs font-medium text-gray-700 uppercase tracking-wider mb-4">{{ $t('booking.step_2.select_date') }}</label>

          <!-- Calendar Header -->
          <div class="flex items-center justify-between mb-4">
            <button
              @click="previousMonth"
              class="p-2 hover:bg-gray-100 transition-colors"
              :disabled="isPreviousMonthDisabled"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h3 class="text-base font-medium text-gray-900">{{ currentMonthLabel }}</h3>
            <button
              @click="nextMonth"
              class="p-2 hover:bg-gray-100 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <!-- Weekday Headers -->
          <div class="grid grid-cols-7 gap-1 mb-2">
            <div v-for="day in weekDays" :key="day" class="text-center text-xs font-medium text-gray-500 py-2">
              {{ day }}
            </div>
          </div>

          <!-- Calendar Days -->
          <div class="grid grid-cols-7 gap-1">
            <button
              v-for="date in calendarDays"
              :key="date.date"
              @click="selectDate(date.date)"
              :disabled="date.isDisabled"
              data-testid="calendar-day"
              :data-date="date.date"
              :class="[
                'py-2 px-1 text-sm transition-colors relative',
                date.isSelected
                  ? 'bg-gray-900 text-white font-medium'
                  : date.isToday
                    ? 'bg-stone-100 text-gray-900 font-medium'
                    : date.isDisabled
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-700 hover:bg-gray-100'
              ]"
            >
              {{ date.day }}
              <!-- Dot indicator for available slots (optional) -->
              <span
                v-if="date.hasAvailability && !date.isSelected && !date.isDisabled"
                class="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-gray-400"
              ></span>
            </button>
          </div>

          <!-- Selected Date Display -->
          <div v-if="selectedDate" class="mt-4 pt-4 border-t border-gray-200">
            <p class="text-sm text-gray-600">
              Selected: <span class="font-medium text-gray-900">{{ formatSelectedDate }}</span>
            </p>
          </div>
        </div>

        <!-- Time Slots -->
        <div v-if="selectedDate" class="border border-gray-200 p-6">
          <h3 class="text-xs font-medium text-gray-900 uppercase tracking-wider mb-4">{{ $t('booking.step_2.available_slots') }}</h3>

          <div v-if="loading" class="text-center py-8">
            <div class="animate-spin h-6 w-6 border-b-2 border-gray-900 mx-auto"></div>
          </div>

          <div v-else-if="error" class="text-center py-8 text-red-600 text-sm">
            {{ error }}
          </div>

          <div
            v-else-if="slots.length === 0"
            data-testid="empty-availability-message"
            class="text-center py-8 text-gray-500 text-sm"
          >
            {{ $t(emptySlotsMessageKey) }}
          </div>

          <div v-else>
            <p v-if="isCrossLocation" class="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 mb-4">
              {{ $t('booking.step_2.arrive_studio_note') }}
            </p>
            <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              <button
                v-for="(slot, index) in slots"
                :key="index"
                @click="selectSlot(slot)"
                data-testid="time-slot"
                :data-studio-time="slot.studio_time || ''"
                :data-photo-time="slot.photo_time || ''"
                :class="[
                  'p-3 border-2 text-left transition-all',
                  isSlotSelected(slot)
                    ? 'border-gray-900 bg-stone-50'
                    : 'border-gray-200 hover:border-gray-400'
                ]"
              >
                <div class="text-sm font-medium">
                  <template v-if="slot.studio_time">
                    {{ $t('booking.step_2.arrive_at_studio', { time: slot.studio_time }) }}
                  </template>
                  <template v-else>
                    {{ slot.photo_time }}
                  </template>
                </div>
                <div v-if="slot.studio_time && slot.photo_time" class="text-xs text-gray-500">
                  {{ $t('booking.step_2.photo') }}: {{ slot.photo_time }}
                </div>
              </button>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex justify-between mt-8">
          <button
            @click="goToStep(1)"
            data-testid="back-to-cart"
            class="inline-flex items-center justify-center px-6 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium tracking-wide hover:bg-gray-50 transition-colors"
          >
            {{ $t('booking.step_2.back_to_select') }}
          </button>
          <button
            @click="goToStep(3)"
            :disabled="!selectedSlot"
            data-testid="continue-to-checkout"
            class="inline-flex items-center justify-center px-6 py-2.5 bg-gray-900 text-white text-sm font-medium tracking-wide hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            {{ $t('booking.step_2.continue_to_checkout') }}
          </button>
        </div>
      </div>

      <!-- Step 3: Checkout -->
      <div v-if="currentStep === 3" data-testid="booking-step-3" class="max-w-3xl mx-auto">
        <!-- Order Summary -->
        <div class="border border-gray-200 p-6 mb-6">
          <h3 class="text-xs font-medium text-gray-900 uppercase tracking-wider mb-4">{{ $t('booking.step_3.order_summary') }}</h3>

          <div class="space-y-3 mb-6">
            <div v-for="item in cartStore.items" :key="item.id" data-testid="checkout-item" :data-package-code="item.package.code" class="flex justify-between text-sm">
              <div>
                <p class="font-medium text-gray-900">{{ getLocalizedPackageName(item.package) }}</p>
              </div>
              <span class="font-medium text-gray-900">฿{{ parseFloat(item.price_at_time).toLocaleString() }}</span>
            </div>
          </div>

          <div class="border-t border-gray-200 pt-4 space-y-2">
            <div class="flex justify-between text-sm text-gray-600">
              <span>{{ $t('booking.step_1.summary.total_price') }}</span>
              <span data-testid="checkout-total">฿{{ cartStore.totalPrice.toLocaleString() }}</span>
            </div>
            <div class="flex justify-between text-gray-900 font-medium text-base">
              <span class="flex-1 pr-4">{{ $t('booking.step_3.deposit_due_now') }}</span>
              <div class="text-right">
                <div class="flex items-center gap-2">
                  <span
                    v-if="cartStore.rawTotalDeposit > cartStore.totalDeposit"
                    class="text-sm text-gray-400 line-through font-normal"
                  >
                    ฿{{ cartStore.rawTotalDeposit.toLocaleString() }}
                  </span>
                  <span data-testid="checkout-deposit">฿{{ cartStore.totalDeposit.toLocaleString() }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Booking Details -->
        <div class="border border-gray-200 p-6 mb-6">
          <h3 class="text-xs font-medium text-gray-900 uppercase tracking-wider mb-4">{{ $t('booking.step_3.booking_details') }}</h3>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <p class="text-xs text-gray-500 uppercase tracking-wider">{{ $t('booking.step_3.date') }}</p>
              <p class="font-medium text-gray-900">{{ formatDisplayDate(selectedDate) }}</p>
            </div>
            <div>
              <p class="text-xs text-gray-500 uppercase tracking-wider">{{ $t('booking.step_3.time') }}</p>
              <p class="font-medium text-gray-900">{{ selectedSlot?.studio_time || '-' }}</p>
            </div>
          </div>
          <div v-if="selectedSlot" class="mt-4 pt-4 border-t border-gray-200">
            <p class="text-xs text-gray-500 uppercase tracking-wider">{{ $t('booking.step_3.photo_session') }}</p>
            <p class="font-medium text-gray-900">{{ selectedSlot.photo_time }}</p>
          </div>
        </div>

        <!-- Login to continue (anonymous users only) -->
        <div
          v-if="!userStore.isLoggedIn && !loginBannerDismissed"
          class="border border-stone-200 bg-stone-50 p-4 mb-6"
        >
          <p class="text-sm font-medium text-gray-900 mb-1">
            {{ $t('booking.step_3.login_prompt.title') }}
          </p>
          <p class="text-xs text-gray-600 mb-3">
            {{ $t('booking.step_3.login_prompt.description') }}
          </p>
          <div class="flex items-center gap-4">
            <button
              @click="showAuthModal = true"
              class="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-xs font-medium tracking-wide hover:bg-gray-800 transition-colors"
            >
              <svg class="w-4 h-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              {{ $t('booking.step_3.login_prompt.sign_in') }}
            </button>
            <button
              @click="loginBannerDismissed = true"
              class="text-xs text-gray-400 underline hover:text-gray-600"
            >
              {{ $t('booking.step_3.login_prompt.skip') }}
            </button>
          </div>
        </div>

        <!-- Pre-fill notice (shown after Google login at step 3) -->
        <div v-if="prefillNotice" class="border border-blue-100 bg-blue-50 p-3 mb-6 text-xs text-blue-700">
          {{ $t('booking.step_3.login_prompt.prefilled_notice') }}
        </div>

        <!-- Contact Information -->
        <div class="border border-gray-200 p-6 mb-6">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-xs font-medium text-gray-900 uppercase tracking-wider">{{ $t('booking.step_3.contact_info') }}</h3>
            <button
              v-if="!isEditMode"
              @click="isEditMode = true"
              class="text-xs text-gray-500 underline hover:text-gray-900 transition-colors"
            >
              {{ $t('common.edit') }}
            </button>
          </div>

          <!-- Error Message -->
          <div v-if="contactError" class="mb-4 bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            {{ $t('booking.step_3.errors.' + contactError) }}
          </div>

          <!-- Read-only display -->
          <div v-if="!isEditMode" class="space-y-4">
            <div class="grid grid-cols-2 gap-x-8 gap-y-4">
              <div>
                <p class="text-xs text-gray-500 uppercase tracking-wider mb-1">{{ $t('booking.step_3.full_name') }}</p>
                <p class="text-sm text-gray-900 font-medium">{{ contactInfo.fullName }}</p>
              </div>
              <div v-if="contactInfo.country">
                <p class="text-xs text-gray-500 uppercase tracking-wider mb-1">{{ $t('booking.step_3.country') }}</p>
                <p class="text-sm text-gray-900">{{ contactInfo.country }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-500 uppercase tracking-wider mb-1">{{ $t('booking.step_3.phone') }}</p>
                <p class="text-sm text-gray-900 font-medium">{{ contactInfo.countryCode }} {{ contactInfo.phoneNumber }}</p>
              </div>
              <div v-if="contactInfo.email">
                <p class="text-xs text-gray-500 uppercase tracking-wider mb-1">{{ $t('booking.step_3.email') }}</p>
                <p class="text-sm text-gray-900">{{ contactInfo.email }}</p>
              </div>
              <div v-if="contactInfo.whatsappNumber">
                <p class="text-xs text-gray-500 uppercase tracking-wider mb-1">WhatsApp</p>
                <p class="text-sm text-gray-900">{{ contactInfo.whatsappNumber }}</p>
              </div>
              <div v-if="contactInfo.isBangkokResident">
                <p class="text-xs text-gray-500 uppercase tracking-wider mb-1">{{ $t('booking.step_3.bangkok_resident_label') }}</p>
                <span class="inline-block text-xs px-2 py-0.5 bg-gray-100 text-gray-700">✓</span>
              </div>
            </div>
            <div v-if="contactInfo.line || contactInfo.instagram || contactInfo.tiktok || contactInfo.facebook" class="pt-3 border-t border-gray-100">
              <p class="text-xs text-gray-500 uppercase tracking-wider mb-2">Social Media</p>
              <div class="flex flex-wrap gap-4 text-sm text-gray-700">
                <span v-if="contactInfo.line">LINE: {{ contactInfo.line }}</span>
                <span v-if="contactInfo.instagram">IG: @{{ contactInfo.instagram }}</span>
                <span v-if="contactInfo.tiktok">TikTok: @{{ contactInfo.tiktok }}</span>
                <span v-if="contactInfo.facebook">FB: {{ contactInfo.facebook }}</span>
              </div>
            </div>
            <!-- No social handle on file: prompt the user to add one (Continue is blocked otherwise) -->
            <div v-else-if="contactErrors.socialMedia" class="pt-3 border-t border-gray-100">
              <p class="text-red-500 text-xs">{{ $t(contactErrors.socialMedia) }}</p>
              <button
                @click="isEditMode = true"
                class="mt-1 text-xs text-gray-600 underline hover:text-gray-900 transition-colors"
              >
                {{ $t('common.edit') }}
              </button>
            </div>

            <!-- Save to Profile Action (for logged-in users with localStorage/new data) -->
            <div v-if="userStore.isLoggedIn && !isFromProfile" class="pt-4 border-t border-gray-100">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <input
                    id="save-to-profile"
                    v-model="saveToProfile"
                    type="checkbox"
                    class="w-4 h-4 text-gray-900 border-gray-300 focus:ring-gray-900"
                  />
                  <label for="save-to-profile" class="text-sm text-gray-700">
                    {{ $t('booking.step_3.save_to_profile') }}
                  </label>
                </div>
                <button
                  v-if="saveToProfile && hasUnsavedChanges"
                  @click="saveContactToProfile"
                  class="text-xs text-gray-600 hover:text-gray-900 underline"
                >
                  {{ $t('booking.step_3.save_now') }}
                </button>
              </div>
              <p class="text-xs text-gray-500 mt-1">
                {{ $t('booking.step_3.save_to_profile_note') }}
              </p>
            </div>

          </div>

          <!-- Edit form -->
          <div v-else class="space-y-4">
            <!-- Full Name -->
            <div>
              <label class="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                {{ $t('booking.step_3.full_name_required') }}
              </label>
              <input
                v-model="contactInfo.fullName"
                data-testid="contact-full-name"
                type="text"
                :placeholder="$t('booking.step_3.full_name_placeholder')"
                class="w-full px-3 py-2 border border-gray-300 focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                :class="{ 'border-red-500': contactErrors.fullName }"
              />
              <p v-if="contactErrors.fullName" class="text-red-500 text-xs mt-1">{{ $t(contactErrors.fullName) }}</p>
            </div>

            <!-- Country -->
            <div>
              <label class="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                {{ $t('booking.step_3.country_required') }}
              </label>
              <input
                v-model="contactInfo.country"
                data-testid="contact-country"
                type="text"
                :placeholder="$t('booking.step_3.country_placeholder')"
                class="w-full px-3 py-2 border border-gray-300 focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm"
                :class="{ 'border-red-500': contactErrors.country }"
              />
              <p v-if="contactErrors.country" class="text-red-500 text-xs mt-1">{{ $t(contactErrors.country) }}</p>
              <p v-else class="text-xs text-gray-500 mt-1">{{ $t('booking.step_3.country_note') }}</p>
            </div>

            <!-- Email -->
            <div>
              <label class="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                {{ $t('booking.step_3.email_required') }}
              </label>
              <input
                v-model="contactInfo.email"
                data-testid="contact-email"
                type="email"
                :placeholder="$t('booking.step_3.email_placeholder')"
                class="w-full px-3 py-2 border border-gray-300 focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm"
                :class="{ 'border-red-500': contactErrors.email }"
              />
              <p v-if="contactErrors.email" class="text-red-500 text-xs mt-1">{{ $t(contactErrors.email) }}</p>
              <p v-else class="text-xs text-gray-500 mt-1">{{ $t('booking.step_3.email_note') }}</p>
            </div>

            <!-- Phone Number -->
            <div>
              <label class="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                {{ $t('booking.step_3.phone_required') }}
              </label>
              <div class="flex gap-2">
                <CountryCodeCombobox
                  v-model="contactInfo.countryCode"
                />
                <input
                  v-model="contactInfo.phoneNumber"
                  data-testid="contact-phone"
                  type="tel"
                  :placeholder="$t('booking.step_3.phone_placeholder')"
                  class="flex-1 px-3 py-2 border border-gray-300 focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm"
                  :class="{ 'border-red-500': contactErrors.phoneNumber }"
                />
              </div>
              <p v-if="contactErrors.phoneNumber" class="text-red-500 text-xs mt-1">{{ $t(contactErrors.phoneNumber) }}</p>
            </div>

            <!-- Bangkok Resident -->
            <div class="flex items-center gap-3">
              <input
                id="bangkok-resident"
                v-model="contactInfo.isBangkokResident"
                type="checkbox"
                class="w-4 h-4 text-gray-900 border-gray-300 focus:ring-gray-900"
              />
              <label for="bangkok-resident" class="text-sm text-gray-700">
                {{ $t('booking.step_3.bangkok_resident') }}
              </label>
            </div>

            <!-- WhatsApp -->
            <div>
              <label class="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                {{ $t('booking.step_3.whatsapp') }}
              </label>
              <input
                :value="contactInfo.whatsappNumber"
                data-testid="contact-whatsapp"
                @input="handleWhatsappInput"
                type="tel"
                inputmode="tel"
                :placeholder="$t('booking.step_3.whatsapp_placeholder')"
                class="w-full px-3 py-2 border border-gray-300 focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm"
                :class="{ 'border-red-500': contactErrors.whatsappNumber || contactErrors.socialMedia }"
              />
              <p v-if="contactErrors.whatsappNumber" class="text-red-500 text-xs mt-1">{{ $t(contactErrors.whatsappNumber) }}</p>
              <p v-else class="text-xs text-gray-500 mt-1">{{ $t('booking.step_3.whatsapp_note') }}</p>
            </div>

            <!-- Social Media (at least one required) -->
            <div class="pt-2">
              <label class="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                {{ $t('booking.step_3.social_media.group_required') }}
              </label>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                    {{ $t('booking.step_3.social_media.line') }}
                  </label>
                  <input
                    v-model="contactInfo.line"
                    data-testid="contact-line"
                    type="text"
                    :placeholder="$t('booking.step_3.social_media.line_placeholder')"
                    class="w-full px-3 py-2 border border-gray-300 focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm"
                    :class="{ 'border-red-500': contactErrors.socialMedia }"
                  />
                </div>
                <div>
                  <label class="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                    {{ $t('booking.step_3.social_media.instagram') }}
                  </label>
                  <div class="relative">
                    <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">@</span>
                    <input
                      v-model="contactInfo.instagram"
                      type="text"
                      :placeholder="$t('booking.step_3.social_media.instagram_placeholder')"
                      class="w-full pl-7 pr-3 py-2 border border-gray-300 focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm"
                      :class="{ 'border-red-500': contactErrors.socialMedia }"
                    />
                  </div>
                </div>
                <div>
                  <label class="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                    {{ $t('booking.step_3.social_media.tiktok') }}
                  </label>
                  <div class="relative">
                    <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">@</span>
                    <input
                      v-model="contactInfo.tiktok"
                      type="text"
                      :placeholder="$t('booking.step_3.social_media.tiktok_placeholder')"
                      class="w-full pl-7 pr-3 py-2 border border-gray-300 focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm"
                      :class="{ 'border-red-500': contactErrors.socialMedia }"
                    />
                  </div>
                </div>
                <div>
                  <label class="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                    {{ $t('booking.step_3.social_media.facebook') }}
                  </label>
                  <input
                    v-model="contactInfo.facebook"
                    type="text"
                    :placeholder="$t('booking.step_3.social_media.facebook_placeholder')"
                    class="w-full px-3 py-2 border border-gray-300 focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm"
                    :class="{ 'border-red-500': contactErrors.socialMedia }"
                  />
                </div>
              </div>
              <p v-if="contactErrors.socialMedia" class="text-red-500 text-xs mt-1">{{ $t(contactErrors.socialMedia) }}</p>
            </div>

            <!-- Save to Profile checkbox (for logged-in users) -->
            <div v-if="userStore.isLoggedIn" class="pt-4 border-t border-gray-200">
              <div class="flex items-center gap-2">
                <input
                  id="save-to-profile-edit"
                  v-model="saveToProfile"
                  type="checkbox"
                  class="w-4 h-4 text-gray-900 border-gray-300 focus:ring-gray-900"
                />
                <label for="save-to-profile-edit" class="text-sm text-gray-700">
                  {{ $t('booking.step_3.save_to_profile') }}
                </label>
              </div>
              <p class="text-xs text-gray-500 mt-1">
                {{ $t('booking.step_3.save_to_profile_note') }}
              </p>
            </div>

          </div>
        </div>

        <!-- Photo Subjects (show only when cart has photo service) -->
        <div v-if="cartStore.hasPhotoService" class="border border-gray-200 p-6 mb-6">
          <h3 class="text-xs font-medium text-gray-900 uppercase tracking-wider mb-1">{{ $t('booking.step_3.photo_subjects.title') }}</h3>
          <p class="text-xs text-gray-500 mb-4">{{ $t('booking.step_3.photo_subjects.description') }}</p>
          <div class="grid grid-cols-2 gap-4">
            <div v-for="key in photoSubjectFields" :key="key">
              <label class="block text-xs text-gray-500 mb-1">{{ $t(`booking.step_3.photo_subjects.labels.${key}`) }}</label>
              <input
                type="number"
                min="0"
                max="10"
                :value="photoSubjects[key]"
                @input="updateSubject(key, parseInt(($event.target as HTMLInputElement).value))"
                class="w-full px-3 py-2 border border-gray-300 focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm text-center"
              />
            </div>
          </div>
        </div>

        <!-- Deposit Policy Disclaimer -->
        <div class="border border-amber-200 bg-amber-50 p-4 mb-6">
          <p class="text-sm text-amber-800 font-medium mb-3">
            ⚠️ {{ $t('booking.step_3.deposit_policy.notice') }}
          </p>
          <label class="flex items-start gap-3 cursor-pointer">
            <input
              id="deposit-policy-accept"
              v-model="depositPolicyAccepted"
              type="checkbox"
              class="mt-0.5 w-4 h-4 text-gray-900 border-gray-300 focus:ring-gray-900 flex-shrink-0"
            />
            <span class="text-sm text-amber-900">
              {{ $t('booking.step_3.deposit_policy.accept') }}
            </span>
          </label>
        </div>

        <!-- Legal Policy Acceptance -->
        <div class="border border-gray-200 bg-stone-50 p-4 mb-6">
          <label class="flex items-start gap-3 cursor-pointer">
            <input
              id="legal-policy-accept"
              v-model="legalPolicyAccepted"
              type="checkbox"
              class="mt-0.5 w-4 h-4 text-gray-900 border-gray-300 focus:ring-gray-900 flex-shrink-0"
            />
            <span class="text-sm text-gray-700">
              {{ $t('booking.step_3.legal_policy.accept_prefix') }}
              <router-link
                to="/terms-of-use"
                target="_blank"
                rel="noopener noreferrer"
                class="text-gray-900 underline underline-offset-2 hover:text-gray-600"
              >
                {{ $t('booking.step_3.legal_policy.terms_of_use') }}
              </router-link>
              {{ $t('booking.step_3.legal_policy.and') }}
              <router-link
                to="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                class="text-gray-900 underline underline-offset-2 hover:text-gray-600"
              >
                {{ $t('booking.step_3.legal_policy.privacy_policy') }}
              </router-link>
            </span>
          </label>
        </div>

        <!-- Actions -->
        <div class="flex justify-between">
          <button
            @click="goToStep(2)"
            data-testid="back-to-appointment"
            class="inline-flex items-center justify-center px-6 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium tracking-wide hover:bg-gray-50 transition-colors"
          >
            {{ $t('booking.step_3.back_to_appoint') }}
          </button>
          <button
            @click="createOrderAndProceed"
            :disabled="submitting || !isContactValid || !depositPolicyAccepted || !legalPolicyAccepted"
            data-testid="continue-to-payment"
            class="inline-flex items-center justify-center px-6 py-2.5 bg-gray-900 text-white text-sm font-medium tracking-wide hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            <span v-if="submitting" class="flex items-center gap-2">
              <span class="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full"></span>
              {{ $t('booking.step_3.creating_order') }}
            </span>
            <span v-else>{{ $t('booking.step_3.continue_to_payment') }}</span>
          </button>
        </div>

        <!-- Create Order Error -->
        <div v-if="createOrderError" class="mt-4 bg-red-50 border border-red-200 p-3 text-sm text-red-700">
          {{ createOrderError }}
        </div>
      </div>

      <!-- Step 4: Payment -->
      <div v-if="currentStep === 4 && createdOrder" data-testid="booking-step-4">
        <PaymentForm
          :order="createdOrder"
          :is-returning-from-3ds="isReturningFrom3ds"
          @payment-success="onPaymentSuccess"
        />

        <!-- Way out of the payment step. Deliberately quiet styling so it never
             competes with the pay button, but always present while a payment is
             still outstanding — including while a PromptPay QR is on screen,
             which is exactly when a customer who changed their mind is stuck.
             Hidden only while a 3DS result is still being verified, where the
             payment outcome is not yet known. -->
        <div v-if="!isReturningFrom3ds" class="mt-10 pt-6 border-t border-gray-100 text-center">
          <button
            @click="showCancelOrderModal = true"
            data-testid="cancel-order"
            class="text-xs text-black underline underline-offset-4 hover:text-gray-700 transition-colors"
          >
            {{ $t('booking.step_4.cancel_order') }}
          </button>
          <p v-if="cancelOrderError" class="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 p-3">
            {{ cancelOrderError }}
          </p>
        </div>
      </div>
    </div>

    <!-- Select Package for Person Modal -->
    <TransitionRoot appear :show="showSelectPackageModal" as="template">
      <Dialog as="div" @close="closeSelectPackageModal" class="relative z-50">
        <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-black/50" />
        </TransitionChild>

        <div class="fixed inset-0 overflow-y-auto">
          <div class="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
            >
              <DialogPanel class="w-full max-w-4xl bg-white overflow-hidden max-h-[90vh] flex flex-col">
                <div class="bg-gray-900 px-6 py-4 flex items-center justify-between flex-shrink-0">
                  <DialogTitle class="text-base font-medium text-white tracking-wide">
                    {{ $t('booking.step_1.select_package') }}
                  </DialogTitle>
                  <button @click="closeSelectPackageModal" class="text-white/80 hover:text-white">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div class="p-6 overflow-y-auto flex-1">
                  <div v-if="loadingPackages" class="text-center py-12">
                    <div class="animate-spin h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                    <p class="mt-4 text-gray-600 text-sm">{{ $t('common.loading') }}</p>
                  </div>

                  <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div
                      v-for="pkg in availablePackages"
                      :key="pkg.id"
                      @click="selectPackageForPerson(pkg)"
                      :class="[
                        'border-2 p-4 cursor-pointer transition-all hover:border-gray-400',
                        selectedPackage?.id === pkg.id
                          ? 'border-gray-900 bg-stone-50'
                          : 'border-gray-200'
                      ]"
                    >
                      <div class="flex items-start justify-between mb-2">
                        <span
                          :class="[
                            'px-2 py-0.5 text-xs font-medium',
                            pkg.package_type === 'premium'
                              ? 'bg-gradient-to-r from-[#60a5fa] via-[#81c4ff] to-[#60a5fa] text-white'
                              : 'bg-[#f6e7d3] text-gray-800'
                          ]"
                        >
                          {{ pkg.package_type }}
                        </span>
                        <p class="text-base font-medium text-gray-900">฿{{ formatPrice(pkg.price) }}</p>
                      </div>
                      <h4 class="font-medium text-gray-900 mb-2 text-sm">{{ pkg.name }}</h4>
                      <div class="flex flex-wrap gap-1">
                        <span
                          v-for="service in pkg.services"
                          :key="service"
                          class="text-xs text-gray-600 bg-stone-50 px-2 py-0.5 border border-gray-100"
                        >
                          {{ getLocalizedService(service) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="bg-stone-50 px-6 py-4 border-t border-gray-200 flex-shrink-0">
                  <div class="flex gap-3 justify-end">
                    <button
                      @click="closeSelectPackageModal"
                      class="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium tracking-wide hover:bg-white transition-colors"
                    >
                      {{ $t('common.cancel') }}
                    </button>
                    <button
                      @click="confirmSelectPackage"
                      :disabled="!selectedPackage"
                      class="inline-flex items-center justify-center px-4 py-2 bg-gray-900 text-white text-sm font-medium tracking-wide hover:bg-gray-800 transition-colors disabled:opacity-50"
                    >
                      {{ $t('booking.step_1.select_package') }}
                    </button>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- Delete Confirmation Modal -->
    <TransitionRoot appear :show="showDeleteModal" as="template">
      <Dialog as="div" @close="closeDeleteModal" class="relative z-50">
        <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-black/50" />
        </TransitionChild>

        <div class="fixed inset-0 overflow-y-auto">
          <div class="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
            >
              <DialogPanel class="w-full max-w-md bg-white overflow-hidden">
                <div class="p-6 text-center">
                  <div class="w-12 h-12 mx-auto mb-4 bg-stone-100 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>

                  <p class="text-xs uppercase tracking-[0.3em] text-gray-500 mb-2">{{ $t('booking.modals.delete.confirm') }}</p>
                  <h3 class="text-lg font-medium text-gray-900 mb-2">{{ $t('booking.modals.delete.remove_title') }}</h3>
                  <p class="text-gray-600 mb-6 text-sm">
                    {{ $t('booking.modals.delete.remove_desc', { name: itemToDelete?.person_name || $t('booking.step_1.unnamed') }) }}
                  </p>

                  <div class="flex gap-3 justify-center">
                    <button
                      @click="closeDeleteModal"
                      class="inline-flex items-center justify-center px-6 py-2 border border-gray-300 text-gray-700 text-sm font-medium tracking-wide hover:bg-gray-50 transition-colors"
                    >
                      {{ $t('common.cancel') }}
                    </button>
                    <button
                      @click="executeDelete"
                      class="inline-flex items-center justify-center px-6 py-2 bg-gray-900 text-white text-sm font-medium tracking-wide hover:bg-gray-800 transition-colors"
                    >
                      {{ $t('booking.modals.delete.remove') }}
                    </button>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- Cancel Order Confirmation Modal (step 4) -->
    <TransitionRoot appear :show="showCancelOrderModal" as="template">
      <Dialog as="div" @close="closeCancelOrderModal" class="relative z-50">
        <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-black/50" />
        </TransitionChild>

        <div class="fixed inset-0 overflow-y-auto">
          <div class="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
            >
              <DialogPanel class="w-full max-w-md bg-white overflow-hidden">
                <div class="p-6 text-center">
                  <div class="w-12 h-12 mx-auto mb-4 bg-stone-100 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>

                  <h3 class="text-lg font-medium text-gray-900 mb-2">{{ $t('booking.modals.cancel_order.title') }}</h3>
                  <p class="text-gray-600 mb-3 text-sm">
                    {{ $t('booking.modals.cancel_order.description') }}
                  </p>
                  <!-- Losing the whole selection is the fear that stops people
                       using an exit like this, so say plainly that it is kept. -->
                  <p class="text-gray-900 mb-6 text-sm bg-stone-50 border border-gray-200 p-3">
                    {{ $t('booking.modals.cancel_order.cart_kept') }}
                  </p>

                  <div class="flex gap-3 justify-center">
                    <button
                      @click="closeCancelOrderModal"
                      data-testid="keep-order"
                      class="inline-flex items-center justify-center px-6 py-2 bg-white border border-gray-300 text-gray-900 text-sm font-medium tracking-wide hover:bg-gray-50 transition-colors"
                    >
                      {{ $t('booking.modals.cancel_order.keep') }}
                    </button>
                    <button
                      @click="cancelOrder"
                      :disabled="cancellingOrder"
                      data-testid="confirm-cancel-order"
                      class="inline-flex items-center justify-center px-6 py-2 bg-red-600 text-white text-sm font-medium tracking-wide hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                      <span v-if="cancellingOrder" class="flex items-center gap-2">
                        <span class="animate-spin inline-block w-4 h-4 border-2 border-white/50 border-t-transparent rounded-full"></span>
                        {{ $t('booking.modals.cancel_order.cancelling') }}
                      </span>
                      <span v-else>{{ $t('booking.modals.cancel_order.confirm') }}</span>
                    </button>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- Change Package Modal -->
    <TransitionRoot appear :show="showChangePackageModal" as="template">
      <Dialog as="div" @close="closeChangePackageModal" class="relative z-50">
        <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-black/50" />
        </TransitionChild>

        <div class="fixed inset-0 overflow-y-auto">
          <div class="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
            >
              <DialogPanel class="w-full max-w-4xl bg-white overflow-hidden max-h-[90vh] flex flex-col">
                <!-- Header -->
                <div class="bg-gray-900 px-6 py-4 flex items-center justify-between flex-shrink-0">
                  <div>
                    <DialogTitle class="text-base font-medium text-white tracking-wide">
                      {{ $t('booking.step_1.change_package') }}
                    </DialogTitle>
                    <p class="text-gray-400 text-sm mt-1">
                      {{ $t(getMainPurposeTitle(currentMainPurpose)) }}
                    </p>
                  </div>
                  <button @click="closeChangePackageModal" class="text-white/80 hover:text-white">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div class="p-6 overflow-y-auto flex-1 bg-stone-50">
                  <div v-if="loadingPackages" class="text-center py-8">
                    <div class="animate-spin h-6 w-6 border-b-2 border-gray-900 mx-auto"></div>
                    <p class="mt-4 text-gray-600 text-sm">{{ $t('common.loading') }}</p>
                  </div>

                  <div v-else-if="availablePackages.length === 0" class="text-center py-12 bg-white border border-gray-100">
                    <div class="w-16 h-16 mx-auto mb-4 bg-gray-50 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                    </div>
                    <p class="text-gray-500 text-sm">{{ $t('packages.filters.no_packages_found') }}</p>
                  </div>

                  <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div
                      v-for="pkg in availablePackages"
                      :key="pkg.id"
                      @click="selectNewPackage(pkg)"
                      data-testid="change-package-option"
                      :data-package-code="pkg.code"
                      :class="[
                        'group bg-white border-2 p-5 cursor-pointer transition-all hover:shadow-xl hover:border-gray-400 flex flex-col',
                        selectedNewPackage?.id === pkg.id
                          ? 'border-gray-900 bg-stone-50'
                          : 'border-gray-200'
                      ]"
                    >
                      <!-- Content Wrapper -->
                      <div class="flex-1">
                        <!-- Package Header -->
                        <div class="flex items-start justify-between mb-3">
                          <span
                            :class="[
                              'px-2.5 py-1 text-xs font-medium uppercase tracking-wide',
                              pkg.package_type === 'premium'
                                ? 'bg-gradient-to-r from-[#60a5fa] via-[#81c4ff] to-[#60a5fa] text-white'
                                : 'bg-[#f6e7d3] text-gray-800'
                            ]"
                          >
                            {{ $t(`packages.filters.${pkg.package_type}`) }}
                          </span>
                          <!-- Gender Avatar -->
                          <img
                            v-if="pkg.gender !== 'any'"
                            :src="getAvatarPath(pkg.age_group, pkg.gender)"
                            :alt="`${pkg.age_group} ${pkg.gender}`"
                            class="w-8 h-8 object-cover rounded-full border border-gray-600"
                          />
                        </div>

                        <!-- Package Name -->
                        <h4 class="text-base font-medium text-gray-900 mb-2 group-hover:text-gray-700 transition-colors line-clamp-2">{{ getLocalizedPackageName(pkg) }}</h4>

                        <!-- Description -->
                        <p v-if="pkg.description_en" class="text-sm text-gray-500 mb-3 line-clamp-2">{{ pkg.description_en }}</p>

                        <!-- Services -->
                        <div class="flex flex-wrap gap-2 mb-4">
                          <span
                            v-for="service in pkg.services"
                            :key="service"
                            :class="[
                              'inline-flex items-center px-2 py-1 text-xs border',
                              currentMainPurpose === 'cosmetic' ? 'bg-pink-50 text-pink-700 border-pink-100' :
                              currentMainPurpose === 'photography' ? 'bg-blue-50 text-blue-700 border-blue-100' :
                              'bg-stone-50 text-gray-600 border-gray-100'
                            ]"
                          >
                            {{ getLocalizedService(service) }}
                          </span>
                        </div>

                        <!-- Duration -->
                        <div class="flex items-center gap-4 text-xs text-gray-500 mb-4">
                          <span class="flex items-center gap-1">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {{ pkg.duration_minutes }} {{ $t('packages.card.min') }}
                          </span>
                        </div>
                      </div>

                      <!-- Price -->
                      <div class="pt-4 border-t border-gray-100">
                        <div class="flex items-end justify-between">
                          <div>
                            <p class="text-xs text-gray-400 mb-1 uppercase tracking-wider">{{ $t('packages.card.price') }}</p>
                            <p class="text-xl font-light text-gray-900">฿{{ formatPrice(pkg.price) }}</p>
                          </div>
                          <span class="text-xs text-gray-400 bg-stone-50 px-2 py-1">
                            {{ $t('packages.card.deposit') }}: ฿{{ calculateDeposit(pkg.services).toLocaleString() }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="bg-white px-6 py-4 border-t border-gray-200 flex-shrink-0">
                  <div class="flex gap-3 justify-end">
                    <button
                      @click="closeChangePackageModal"
                      class="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium tracking-wide hover:bg-gray-50 transition-colors"
                    >
                      {{ $t('common.cancel') }}
                    </button>
                    <button
                      @click="confirmChangePackage"
                      :disabled="!selectedNewPackage"
                      data-testid="confirm-package-change"
                      class="inline-flex items-center justify-center px-4 py-2 bg-gray-900 text-white text-sm font-medium tracking-wide hover:bg-gray-800 transition-colors disabled:opacity-50"
                    >
                      {{ $t('booking.step_1.change_package') }}
                    </button>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- Auth Modal (for anonymous users in step 3) -->
    <AuthModal
      :is-open="showAuthModal"
      @close="showAuthModal = false"
      @success="handleLoginSuccess"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useDateFormatter } from '@/composables/useDateFormatter'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { useCartStore, PHOTO_ASSIGNMENT_LIMIT_CODE } from '@/stores/cart'
import { useUserStore } from '@/stores/user'
import { useAuthStore } from '@/stores/auth'
import { useContactInfo } from '@/composables/useContactInfo'
import { usePhotoSubjects } from '@/composables/usePhotoSubjects'
import { SERVICE_TRANSLATION_KEYS, SERVICE_DESCRIPTIONS, type ServiceCode } from '@/constants/services'
import { availabilityEmptyMessageKey } from '@/utils/availabilityMessage'
import BookingStepper from '@/components/BookingStepper.vue'
import PaymentForm from '@/components/PaymentForm.vue'
import AuthModal from '@/components/AuthModal.vue'
import CountryCodeCombobox from '@/components/CountryCodeCombobox.vue'
import type { Package, CartItem, AvailabilitySlot, AvailabilityResponse } from '@/types'

const { t, locale } = useI18n()

const API_BASE_URL = '/api/v1'

const router = useRouter()
const route = useRoute()
const cartStore = useCartStore()
const userStore = useUserStore()
const authStore = useAuthStore()
const {
  contactInfo,
  isValid: isContactValid,
  validationErrors: contactErrors,
  hasExistingData,
  isFromProfile,
  hasUnsavedChanges,
  error: contactError,
  saveToProfile: saveContactToProfile,
  updateField,
  initFromUserStore
} = useContactInfo()

const { subjects: photoSubjects, updateSubject, reset: resetPhotoSubjects, toPayload: photoSubjectsPayload } = usePhotoSubjects()

const photoSubjectFields = ['male', 'female', 'boy', 'girl', 'senior_male', 'senior_female'] as const

function handleWhatsappInput(e: Event) {
  const sanitized = (e.target as HTMLInputElement).value.replace(/[^+\d]/g, '')
  updateField('whatsappNumber', sanitized)
}

// Always start in edit mode for convenience (logged-in users can edit pre-filled data)
const isEditMode = ref(true)

// Sync authStore user to userStore (userStore.contactInfo is derived from userStore.user)
// and initialize contact info from userStore
watch(() => authStore.user, (authUser) => {
  if (authUser && authStore.isAuthenticated) {
    // Update userStore with authStore data
    userStore.setUser({
      id: authUser.id,
      email: authUser.email,
      name: authUser.name,
      role: authUser.role,
      // Add any other fields that exist in both stores
      country_code: (authUser as any).country_code,
      phone: (authUser as any).phone,
      is_bangkok_resident: (authUser as any).is_bangkok_resident,
      whatsapp_number: (authUser as any).whatsapp_number,
      social_links: (authUser as any).social_links
    })
  }
}, { immediate: true })

// Initialize contact info from user profile if logged in
// This will pre-fill the form with profile data
initFromUserStore()

// Save to profile checkbox state (only for logged-in users with non-profile data)
const saveToProfile = ref(userStore.isLoggedIn && !isFromProfile.value)

// Watch for user login state changes and profile data availability
watch(() => userStore.isLoggedIn, (isLoggedIn) => {
  if (isLoggedIn && !isFromProfile.value) {
    // If user logs in and data is not from profile, suggest saving to profile
    saveToProfile.value = true
  }
})

// When data source switches to 'profile' (e.g. auth loads async), uncheck the checkbox
watch(isFromProfile, (fromProfile) => {
  if (fromProfile) saveToProfile.value = false
})

// Login banner state (step 3)
const loginBannerDismissed = ref(false)
const showAuthModal = ref(false)
const prefillNotice = ref(false)

// Current Step - initialized from store, will be restored from server on mount
const currentStep = ref(cartStore.currentStep)

// Delete Modal State
const showDeleteModal = ref(false)
const itemToDelete = ref<CartItem | null>(null)

// Change Package Modal State
const showChangePackageModal = ref(false)
const itemToChangePackage = ref<CartItem | null>(null)
const availablePackages = ref<Package[]>([])
const selectedNewPackage = ref<Package | null>(null)
const loadingPackages = ref(false)
const currentMainPurpose = ref<string>('')

// Select Package for Person Modal State
const showSelectPackageModal = ref(false)
const itemToSelectPackage = ref<CartItem | null>(null)
const selectedPackage = ref<Package | null>(null)

// DateTime Step State
const selectedDate = ref('')
const selectedSlot = ref<AvailabilitySlot | null>(null)
const isRestoringFromServer = ref(false)
const slots = ref<AvailabilitySlot[]>([])
const isBusinessClosed = ref(false)
const emptySlotsMessageKey = computed(() => availabilityEmptyMessageKey(isBusinessClosed.value))
const isCrossLocation = computed(() => slots.value.some(s => s.studio_time && s.photo_time))
const loading = ref(false)
const error = ref<string | null>(null)
const submitting = ref(false)
const depositPolicyAccepted = ref(false)
const legalPolicyAccepted = ref(false)

// Payment step state
const createdOrder = ref<{ id: number; access_token: string; booking_date: string; studio_slot_time: string; photo_slot_time: string; total_deposit: string; total_price: string } | null>(null)
const createOrderError = ref('')
const isReturningFrom3ds = ref(false)

// Cancel-order state (step 4)
const showCancelOrderModal = ref(false)
const cancellingOrder = ref(false)
const cancelOrderError = ref('')

// Session storage key for persisting order during payment step
const ORDER_STORAGE_KEY = 'booking_created_order'

// Calendar State
const currentCalendarMonth = ref(new Date())
const { formatDate, formatMonthYear, getWeekdayShortNames } = useDateFormatter()

// Weekday names localized based on current locale
const weekDays = computed(() => getWeekdayShortNames())

const currentMonthLabel = computed(() => {
  return formatMonthYear(currentCalendarMonth.value)
})

const isPreviousMonthDisabled = computed(() => {
  const today = new Date()
  today.setDate(1)
  today.setHours(0, 0, 0, 0)
  const currentMonth = new Date(currentCalendarMonth.value)
  currentMonth.setDate(1)
  currentMonth.setHours(0, 0, 0, 0)
  return currentMonth <= today
})

const formatSelectedDate = computed(() => {
  return formatDate(selectedDate.value)
})

const calendarDays = computed(() => {
  const year = currentCalendarMonth.value.getFullYear()
  const month = currentCalendarMonth.value.getMonth()

  // Get first day of month
  const firstDay = new Date(year, month, 1)
  // Get last day of month
  const lastDay = new Date(year, month + 1, 0)

  // Get day of week for first day (0 = Sunday)
  const startDayOfWeek = firstDay.getDay()

  // Get days from previous month
  const daysFromPrevMonth = startDayOfWeek
  const prevMonthLastDay = new Date(year, month, 0).getDate()

  const days = []
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  // Add days from previous month
  for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
    const day = prevMonthLastDay - i
    const date = new Date(year, month - 1, day)
    days.push({
      date: formatDateForComparison(date),
      day: day,
      isSelected: false,
      isToday: false,
      isDisabled: true,
      isCurrentMonth: false,
      hasAvailability: false
    })
  }

  // Add days from current month
  for (let day = 1; day <= lastDay.getDate(); day++) {
    const date = new Date(year, month, day)
    const dateStr = formatDateForComparison(date)
    const isDisabled = date <= today

    days.push({
      date: dateStr,
      day: day,
      isSelected: selectedDate.value === dateStr,
      isToday: date.getTime() === today.getTime(),
      isDisabled: isDisabled,
      isCurrentMonth: true,
      hasAvailability: false // Could be populated from API
    })
  }

  // Add days from next month to fill the grid
  const remainingDays = 42 - days.length // 6 rows * 7 columns
  for (let day = 1; day <= remainingDays; day++) {
    const date = new Date(year, month + 1, day)
    days.push({
      date: formatDateForComparison(date),
      day: day,
      isSelected: false,
      isToday: false,
      isDisabled: true,
      isCurrentMonth: false,
      hasAvailability: false
    })
  }

  return days
})

function formatDateForComparison(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatDisplayDate(dateStr: string): string {
  return formatDate(dateStr)
}

function previousMonth() {
  if (isPreviousMonthDisabled.value) return
  currentCalendarMonth.value = new Date(
    currentCalendarMonth.value.getFullYear(),
    currentCalendarMonth.value.getMonth() - 1,
    1
  )
}

function nextMonth() {
  currentCalendarMonth.value = new Date(
    currentCalendarMonth.value.getFullYear(),
    currentCalendarMonth.value.getMonth() + 1,
    1
  )
}

async function selectDate(dateStr: string) {
  selectedDate.value = dateStr
  // Sync to cart immediately
  await cartStore.setBookingDate(dateStr)
}

// Restore step from server on mount and validate it
onMounted(async () => {
  // Handle return from Omise 3DS redirect.
  // sessionStorage (not localStorage) — auto-clears when tab closes.
  // The guest access token remains in sessionStorage and is never exposed in
  // the gateway return URL.
  if (route.query.payment_return === 'true' && route.query.order_id) {
    const savedOrder = sessionStorage.getItem('pending_payment_order')
    if (savedOrder) {
      try {
        const orderData = JSON.parse(savedOrder)
        if (String(orderData.id) === String(route.query.order_id)) {
          createdOrder.value = orderData
          isReturningFrom3ds.value = true
          currentStep.value = 4
          sessionStorage.removeItem('pending_payment_order')
          // Clean up URL
          router.replace({ path: '/booking' })
          return
        }
      } catch {
        sessionStorage.removeItem('pending_payment_order')
      }
    }
  }

  // Wait for cart to be fetched
  await cartStore.fetchCart()

  // Restore created order if we're on step 4 (payment step reload)
  const savedOrder = sessionStorage.getItem(ORDER_STORAGE_KEY)
  if (savedOrder) {
    try {
      createdOrder.value = JSON.parse(savedOrder)
    } catch {
      sessionStorage.removeItem(ORDER_STORAGE_KEY)
    }
  }

  // Set flag to prevent watcher from clearing selectedSlot during restore
  isRestoringFromServer.value = true

  // First restore date/time from server (needed for step validation)
  if (cartStore.cartState.bookingDate) {
    selectedDate.value = cartStore.cartState.bookingDate
  }
  if (cartStore.cartState.timeSlot) {
    cartStore.primaryTimeSlot = cartStore.cartState.timeSlot
  }

  // Restore step from server
  const savedStep = cartStore.cartState.currentStep || 1

  // Validate the saved step
  // Special case: allow step 4 even if cart is empty (order already created)
  if (savedStep === 4 && createdOrder.value) {
    // Valid payment step with order - restore it
    currentStep.value = 4
  } else if (savedStep > 1 && cartStore.isEmpty) {
    // Can't be beyond step 1 if cart is empty
    currentStep.value = 1
    cartStore.setCurrentStep(1)
  } else if (savedStep > 1 && hasItemsWithoutPackage.value) {
    // Can't be beyond step 1 if items don't have packages
    currentStep.value = 1
    cartStore.setCurrentStep(1)
  } else if (savedStep === 3 && (!selectedDate.value || !cartStore.primaryTimeSlot)) {
    // Can't be on step 3 if date/time not selected
    currentStep.value = 2
    cartStore.setCurrentStep(2)
  } else if (savedStep === 4 && !createdOrder.value) {
    // Can't be on step 4 if no order created
    // Empty cart = payment completed, fresh start; has items = session lost, re-checkout
    if (cartStore.isEmpty) {
      currentStep.value = 1
      cartStore.setCurrentStep(1)
    } else {
      currentStep.value = 3
      cartStore.setCurrentStep(3)
    }
  } else {
    // Valid step, restore it
    currentStep.value = savedStep
  }

  // If we have a saved date and time slot, restore the selected slot (for step 2 and 3)
  if ((currentStep.value === 2 || currentStep.value === 3) && selectedDate.value && cartStore.primaryTimeSlot) {
    await fetchSlotsForDate(selectedDate.value)
    // Find slot by studio_time (which is saved to cart)
    const savedSlot = slots.value.find(s => (s.studio_time || s.photo_time) === cartStore.primaryTimeSlot)
    if (savedSlot) {
      selectedSlot.value = savedSlot
    }
    // Sync calendar to show the correct month (only for step 2)
    if (currentStep.value === 2) {
      const date = new Date(selectedDate.value + 'T00:00:00')
      currentCalendarMonth.value = new Date(date.getFullYear(), date.getMonth(), 1)
    }
  }

  // Clear restore flag
  isRestoringFromServer.value = false
})

// Sync calendar and restore saved booking details when entering Appoint step
// Also persist current step to server when it changes (except step 3 which is handled in goToStep)
watch(currentStep, async (step, oldStep) => {
  // Persist step change to server (skip step 3 as it's handled in goToStep with date/time sync)
  if (oldStep !== undefined && step !== oldStep && step !== 3) {
    // The server revalidates the slot whenever a time is attached, so this can
    // reject with a 409 that the store rethrows. Stepping backwards is not the
    // moment to block on that — the store has already cleared the stale
    // date/time, and step 2 re-fetches availability when the user gets there.
    try {
      await cartStore.setCurrentStep(step)
    } catch (err) {
      console.warn('[Booking] Could not persist step change:', err)
    }
  }

  // Re-fetch cart when entering step 1 to ensure fresh data (for multi-tab support)
  if (step === 1 && oldStep !== undefined) {
    await cartStore.fetchCart()
  }

  if (step === 2) {
    // Set flag to prevent date watcher from clearing selectedSlot during restore
    isRestoringFromServer.value = true

    // Restore saved booking date if exists
    if (cartStore.cartState.bookingDate && !selectedDate.value) {
      selectedDate.value = cartStore.cartState.bookingDate
    }

    // Always re-fetch slots when entering step 2 to get real-time availability
    if (selectedDate.value) {
      await fetchSlotsForDate(selectedDate.value)

      // Find and select the saved slot by studio_time (if still available)
      if (cartStore.primaryTimeSlot) {
        const savedSlot = slots.value.find(s => (s.studio_time || s.photo_time) === cartStore.primaryTimeSlot)
        if (savedSlot) {
          selectedSlot.value = savedSlot
        } else {
          // Slot is no longer available, clear selection
          selectedSlot.value = null
        }
      }
    }

    // Clear restore flag after restore is done
    isRestoringFromServer.value = false

    // Sync calendar to show the correct month
    if (selectedDate.value) {
      const date = new Date(selectedDate.value + 'T00:00:00')
      currentCalendarMonth.value = new Date(date.getFullYear(), date.getMonth(), 1)
    }
  }
})

// Sort items by main_purpose (cosmetic -> costume_set -> photography), then by age group (adult -> elderly -> child)
const sortedItems = computed(() => {
  const mainPurposeOrder: Record<string, number> = {
    cosmetic: 1,
    costume_set: 2,
    photography: 3
  }

  const ageGroupOrder: Record<string, number> = {
    adult: 1,
    elderly: 2,
    child: 3
  }

  return [...cartStore.items].sort((a, b) => {
    // First sort by main_purpose
    const mainPurposeA = mainPurposeOrder[a.package.main_purpose] || 99
    const mainPurposeB = mainPurposeOrder[b.package.main_purpose] || 99

    if (mainPurposeA !== mainPurposeB) {
      return mainPurposeA - mainPurposeB
    }

    // Then sort by age_group
    const orderA = ageGroupOrder[a.package.age_group] || 99
    const orderB = ageGroupOrder[b.package.age_group] || 99
    return orderA - orderB
  })
})

// Check if all items have packages
const hasItemsWithoutPackage = computed(() => {
  return cartStore.items.some(item => !item.package_id || item.package_id === 0)
})

// Show photography recommendation when no photography package is selected
const showPhotographyRecommendation = computed(() => {
  // Don't show if cart is empty
  if (cartStore.isEmpty) return false

  // Check if any item has main_purpose === 'photography'
  const hasPhotographyMainPurpose = cartStore.items.some(item =>
    item.package?.main_purpose === 'photography'
  )

  // Check if any item has 'photo' service
  const hasPhotographyService = cartStore.items.some(item =>
    item.package?.services?.includes('photo')
  )

  // Show recommendation only when both conditions are false
  return !hasPhotographyMainPurpose && !hasPhotographyService
})

const canProceedToAppoint = computed(() => {
  return !cartStore.isEmpty && !hasItemsWithoutPackage.value
})

const minDate = computed(() => {
  const today = new Date()
  return today.toISOString().split('T')[0]
})

// Step error for displaying backend validation errors
const stepError = ref('')

// Step Navigation
async function goToStep(step: number) {
  // Clear any previous step error
  stepError.value = ''

  // Validate before going to step 2
  if (step === 2 && !canProceedToAppoint.value) {
    return
  }

  // Validate before going to step 3
  if (step === 3 && (!selectedDate.value || !selectedSlot.value)) {
    return
  }

  // When proceeding to checkout (step 3), sync all booking details including step
  if (step === 3 && selectedDate.value && selectedSlot.value) {
    // Update local state first (save studio_time as primary time slot)
    cartStore.bookingDate = selectedDate.value
    cartStore.primaryTimeSlot = selectedSlot.value.studio_time || selectedSlot.value.photo_time
    cartStore.currentStep = step

    try {
      // Sync all at once to server - backend will validate slot availability
      await cartStore.syncBookingDetails()
    } catch (err: any) {
      // Backend returned error (e.g., slot no longer available)
      if (err.message?.includes('no longer available')) {
        stepError.value = err.message
        // Refresh slots to get latest availability
        await fetchSlotsForDate(selectedDate.value)
        // Stay on step 2 and clear selection
        selectedSlot.value = null
        cartStore.primaryTimeSlot = ''
        window.scrollTo({ top: 0, behavior: 'smooth' })
        return
      }
      // For other errors, still proceed but log
      console.error('Failed to sync booking details:', err)
    }
  } else {
    // For other step changes, just update the step
    await cartStore.setCurrentStep(step)
  }

  currentStep.value = step
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

// Delete Functions
function confirmDelete(item: CartItem) {
  itemToDelete.value = item
  showDeleteModal.value = true
}

function closeDeleteModal() {
  showDeleteModal.value = false
  itemToDelete.value = null
}

function executeDelete() {
  if (itemToDelete.value) {
    cartStore.removeItem(itemToDelete.value.id)
  }
  closeDeleteModal()
}

// Select Package for Person Functions
function selectPackageForItem(item: CartItem) {
  itemToSelectPackage.value = item
  showSelectPackageModal.value = true
  selectedPackage.value = null
  loadingPackages.value = true

  fetchPackagesForPerson(item.package.age_group, item.package.gender, item.package.main_purpose)
}

async function fetchPackagesForPerson(ageGroup: string, gender: string, mainPurpose?: string) {
  try {
    const params = new URLSearchParams()
    params.append('age_group', ageGroup)
    params.append('gender', gender)
    if (mainPurpose) {
      params.append('main_purpose', mainPurpose)
    }

    const response = await fetch(`${API_BASE_URL}/packages?${params}`, {
      credentials: 'include'
    })
    const data = await response.json()

    if (response.ok) {
      availablePackages.value = data.packages
    }
  } catch (err) {
    console.error('Failed to load packages:', err)
  } finally {
    loadingPackages.value = false
  }
}

function closeSelectPackageModal() {
  showSelectPackageModal.value = false
  itemToSelectPackage.value = null
  selectedPackage.value = null
  availablePackages.value = []
}

function selectPackageForPerson(pkg: Package) {
  selectedPackage.value = pkg
}

function confirmSelectPackage() {
  if (!itemToSelectPackage.value || !selectedPackage.value) return

  const updated = cartStore.updateItem(itemToSelectPackage.value.id, {
    package_id: selectedPackage.value.id,
    package: selectedPackage.value,
    price_at_time: selectedPackage.value.price,
    selected_services: selectedPackage.value.services
  })

  if (updated) closeSelectPackageModal()
}

// Change Package Functions
async function openChangePackageModal(item: CartItem) {
  itemToChangePackage.value = item
  showChangePackageModal.value = true
  selectedNewPackage.value = null
  loadingPackages.value = true
  currentMainPurpose.value = item.package.main_purpose || ''

  await fetchPackagesForChange(item.package.main_purpose)
}

async function fetchPackagesForChange(mainPurpose?: string) {
  try {
    const params = new URLSearchParams()
    if (mainPurpose) {
      params.append('main_purpose', mainPurpose)
    }

    const response = await fetch(`${API_BASE_URL}/packages?${params}`, {
      credentials: 'include'
    })
    const data = await response.json()

    if (response.ok) {
      availablePackages.value = data.packages
    }
  } catch (err) {
    console.error('Failed to load packages:', err)
  } finally {
    loadingPackages.value = false
  }
}

function getMainPurposeTitle(mainPurpose: string): string {
  const keys: Record<string, string> = {
    cosmetic: 'packages.cosmetic.title',
    costume_set: 'packages.costume_set.title',
    photography: 'packages.photography.title'
  }
  return keys[mainPurpose] || 'packages.title'
}

function closeChangePackageModal() {
  showChangePackageModal.value = false
  itemToChangePackage.value = null
  selectedNewPackage.value = null
  availablePackages.value = []
  currentMainPurpose.value = ''
}

function selectNewPackage(pkg: Package) {
  selectedNewPackage.value = pkg
}

function confirmChangePackage() {
  if (!itemToChangePackage.value || !selectedNewPackage.value) return

  const updated = cartStore.updateItem(itemToChangePackage.value.id, {
    package_id: selectedNewPackage.value.id,
    package: selectedNewPackage.value,
    price_at_time: selectedNewPackage.value.price,
    selected_services: selectedNewPackage.value.services
  })

  if (updated) closeChangePackageModal()
}

// DateTime Functions
async function selectSlot(slot: AvailabilitySlot) {
  selectedSlot.value = slot
  // Use studio_time if available (cross-location / studio packages), otherwise fall back to photo_time
  await cartStore.setPrimaryTimeSlot(slot.studio_time || slot.photo_time)
}

// Check if a slot is currently selected (compare by primary time key)
function slotKey(slot: AvailabilitySlot): string | undefined {
  return slot.studio_time || slot.photo_time
}

function isSlotSelected(slot: AvailabilitySlot): boolean {
  if (!selectedSlot.value) return false
  return slotKey(selectedSlot.value) === slotKey(slot)
}

// Fetch availability for a specific date
async function fetchSlotsForDate(date: string): Promise<void> {
  loading.value = true
  error.value = null
  isBusinessClosed.value = false

  try {
    const packageIds = [...new Set(
      cartStore.items.map(i => i.package_id).filter(Boolean)
    )]
    if (packageIds.length === 0) return

    const params = new URLSearchParams({ date })
    packageIds.forEach(id => params.append('package_ids[]', String(id)))

    const response = await fetch(`${API_BASE_URL}/availability?${params}`)
    const data: AvailabilityResponse = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch availability')
    }

    slots.value = data.available_slots
    isBusinessClosed.value = Boolean(data.business_closed)
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'An error occurred'
  } finally {
    loading.value = false
  }
}

// Fetch availability when date changes
watch(selectedDate, async (newDate) => {
  if (!newDate) return

  // Don't clear slot if we're restoring from server (avoid race condition)
  if (isRestoringFromServer.value) return

  selectedSlot.value = null
  await fetchSlotsForDate(newDate)
})

// Watch for booking date changes (cleared when packages change)
// If booking date was cleared and user is in step 2 or 3, force redirect to step 1
watch(() => cartStore.bookingDate, (newDate, oldDate) => {
  // If booking date was cleared (from package change) and we're beyond step 1
  if (!newDate && oldDate && currentStep.value > 1) {
    console.log('[Booking] Booking date cleared due to package change, redirecting to step 1')

    // Clear local state
    selectedDate.value = ''
    selectedSlot.value = null

    // Force redirect to step 1
    currentStep.value = 1
    cartStore.setCurrentStep(1)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
})

// Checkout Functions
async function createOrderAndProceed() {
  if (!isContactValid.value) return
  if (!depositPolicyAccepted.value || !legalPolicyAccepted.value) return

  createOrderError.value = ''
  submitting.value = true

  try {
    // prepareOrderPayload() already returns { order: {...} }
    // We need to merge contact_info into the existing order object
    const basePayload = cartStore.prepareOrderPayload()
    const payload = {
      order: {
        ...basePayload.order,
        contact_info: {
          email: contactInfo.value.email,
          full_name: contactInfo.value.fullName,
          country: contactInfo.value.country,
          country_code: contactInfo.value.countryCode,
          phone_number: contactInfo.value.phoneNumber,
          is_bangkok_resident: contactInfo.value.isBangkokResident,
          whatsapp_number: contactInfo.value.whatsappNumber,
          social_media: {
            line: contactInfo.value.line,
            instagram: contactInfo.value.instagram,
            tiktok: contactInfo.value.tiktok,
            facebook: contactInfo.value.facebook
          }
        },
        ...(cartStore.hasPhotoService && photoSubjectsPayload() ? { photo_subjects: photoSubjectsPayload() } : {})
      },
      update_profile: saveToProfile.value && userStore.isLoggedIn
    }

    const response = await fetch(`${API_BASE_URL}/orders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    })

    const data = await response.json()

    if (!response.ok) {
      // Same invariant the cart enforces — show the same modal here rather than a
      // raw English server string on the final step.
      if (data.code === PHOTO_ASSIGNMENT_LIMIT_CODE) {
        cartStore.openPhotoAssignmentLimitModal()
        createOrderError.value = t('booking.modals.photo_limit.separate_booking')
        return
      }
      throw new Error(data.error || 'Failed to create order')
    }

    // Store created order and proceed to payment step
    createdOrder.value = {
      id: data.id,
      access_token: data.access_token,
      booking_date: data.booking_date,
      studio_slot_time: data.studio_slot_time,
      photo_slot_time: data.photo_slot_time,
      total_deposit: data.total_deposit,
      total_price: data.total_price
    }
    // Persist to sessionStorage for page reloads
    sessionStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(createdOrder.value))
    currentStep.value = 4
    window.scrollTo({ top: 0, behavior: 'smooth' })
  } catch (err) {
    createOrderError.value = err instanceof Error ? err.message : 'An error occurred. Please try again.'
  } finally {
    submitting.value = false
  }
}

// Handle Google login success at step 3 — pre-fill form and show notice
function handleLoginSuccess() {
  if (authStore.user) {
    if (!contactInfo.value.fullName) contactInfo.value.fullName = authStore.user.name || ''
    if (!contactInfo.value.email)    contactInfo.value.email    = authStore.user.email || ''
    prefillNotice.value = true
  }
  saveToProfile.value = false
}

function closeCancelOrderModal() {
  showCancelOrderModal.value = false
}

// Lets a customer out of the payment step without abandoning the order silently.
// The server cancels it, which releases the slot straight away — a pending order
// keeps holding its capacity until the orphan cron runs half an hour later — and
// puts the order's items back in the cart so nothing has to be picked again.
async function cancelOrder() {
  if (!createdOrder.value) return

  cancellingOrder.value = true
  cancelOrderError.value = ''

  const token = createdOrder.value.access_token
  const query = token ? `?access_token=${encodeURIComponent(token)}` : ''

  try {
    const response = await fetch(`${API_BASE_URL}/orders/${createdOrder.value.id}/cancel${query}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include'
    })

    const data = await response.json().catch(() => ({}))

    if (!response.ok) {
      // Payment landed in the gap between opening the modal and confirming it.
      // The booking is real, so finish the flow rather than stranding the
      // customer on a payment screen they can no longer act on. PaymentForm may
      // reach the same conclusion from its own polling a moment later; arriving
      // at the thank-you page twice is harmless.
      if (data.code === 'already_paid') {
        closeCancelOrderModal()
        await onPaymentSuccess()
        return
      }
      if (data.code === 'payment_in_progress') {
        closeCancelOrderModal()
        cancelOrderError.value = t('booking.step_4.errors.payment_in_progress')
        return
      }
      throw new Error(data.error || 'Failed to cancel the booking')
    }

    // Drop every trace of the order first: clearing createdOrder unmounts
    // PaymentForm, whose onUnmounted stops the PromptPay polling for us.
    sessionStorage.removeItem(ORDER_STORAGE_KEY)
    sessionStorage.removeItem('pending_payment_order')
    createdOrder.value = null
    createOrderError.value = ''
    closeCancelOrderModal()

    await cartStore.fetchCart()
    selectedSlot.value = null
    currentStep.value = 1
    window.scrollTo({ top: 0, behavior: 'smooth' })
  } catch (err) {
    cancelOrderError.value = err instanceof Error ? err.message : 'An error occurred. Please try again.'
    closeCancelOrderModal()
  } finally {
    cancellingOrder.value = false
  }
}

async function onPaymentSuccess() {
  // Capture all data before clearing state
  const orderDetails: Record<string, string> = {
    order_id: String(createdOrder.value?.id || ''),
    token: createdOrder.value?.access_token || '',
    name: contactInfo.value.fullName || 'Guest',
    date: createdOrder.value?.booking_date || selectedDate.value,
    studio_time: createdOrder.value?.studio_slot_time || selectedSlot.value?.studio_time || '',
    photo_time: createdOrder.value?.photo_slot_time || '',
    packages: String(cartStore.itemCount),
    services: cartStore.items
      .flatMap(item => item.package?.services || [])
      .filter((s, i, arr) => arr.indexOf(s) === i)
      .join(','),
    total: String(createdOrder.value?.total_price || ''),
    deposit: String(createdOrder.value?.total_deposit || '')
  }

  // Clear persisted order and local state after capturing
  sessionStorage.removeItem(ORDER_STORAGE_KEY)
  await cartStore.clearCart()
  resetPhotoSubjects()
  selectedDate.value = ''
  selectedSlot.value = null

  router.push({
    path: '/booking/thank-you',
    query: orderDetails
  })
}

// Helper Functions
function getAgeGroupLabel(value: string, age?: number): string {
  const labels: Record<string, string> = {
    adult: 'Adult',
    child: 'Child',
    elderly: 'Elderly'
  }

  // For children, show detailed age if available
  if (value === 'child' && age !== undefined) {
    if (age === 0) return 'Child (แรกเกิด)'
    if (age === 1) return 'Child (1-2 ขวบ)'
    if (age === 3) return 'Child (3-10 ขวบ)'
  }

  return labels[value] || value
}

function getGenderLabel(value: string): string {
  const labels: Record<string, string> = {
    female: 'Female',
    male: 'Male'
  }
  return labels[value] || value
}

function getAgeGenderLabel(pkg: Package): string {
  const parts: string[] = []

  // Add age_group if not 'any' and not 'adult'
  if (pkg.age_group && pkg.age_group !== 'any' && pkg.age_group !== 'adult') {
    parts.push(t(`packages.filters.${pkg.age_group}`))
  }

  // Add gender if not 'any'
  if (pkg.gender && pkg.gender !== 'any') {
    parts.push(t(`packages.filters.${pkg.gender}`))
  }

  return parts.join(' · ')
}

function getMainPurposeLabel(value: string): string {
  const labels: Record<string, string> = {
    cosmetic: 'Cosmetic',
    costume_set: 'Costume Set',
    photography: 'Photography'
  }
  return labels[value] || value
}

function getMainPurposeImage(value: string): string {
  const images: Record<string, string> = {
    cosmetic: 'https://achiraya-studio-public.s3.ap-southeast-7.amazonaws.com/assets/mp/cosmetic.webp',
    costume_set: 'https://achiraya-studio-public.s3.ap-southeast-7.amazonaws.com/assets/mp/costume_set.webp',
    photography: 'https://achiraya-studio-public.s3.ap-southeast-7.amazonaws.com/assets/mp/photography.webp'
  }
  return images[value] || ''
}

function getAvatarPath(ageGroup: string, gender: string): string {
  const genderLetter = gender === 'female' ? 'f' : 'm'
  const age = ageGroup || 'adult'
  return `/icons/${age}-${genderLetter}.png`
}

function formatPrice(price: string): string {
  return parseFloat(price).toLocaleString()
}

function getLocalizedPackageName(pkg: Package): string {
  const currentLocale = locale.value
  if (currentLocale === 'th' && pkg.name_th) return pkg.name_th
  if (currentLocale === 'zh' && pkg.name_zh) return pkg.name_zh
  return pkg.name_en || pkg.name
}

function getLocalizedService(service: string): string {
  const key = SERVICE_TRANSLATION_KEYS[service as ServiceCode]
  return key ? t(key) : service
}

function calculateDeposit(services: string[]): number {
  const hasPhoto = services.includes('photo')
  const hasMakeup = services.includes('makeup')
  const hasDress = services.includes('dress')

  if (hasPhoto) return 1000
  if (hasMakeup || hasDress) return 500
  return 0
}
</script>
